import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import type { PedidoSuggestion } from '@/features/processo-arbitral/types/conflictTypes';
import type { Contact, ContactSearchResult } from '@/features/processo-arbitral/types/contactTypes';
import type { CustasPorRito } from '@/features/processo-arbitral/types/custasTypes';
import type {
  ConflictDocument,
  ConflictDocumentSubtipo,
  DocumentsFormData,
  ProofDocumentRequirement,
  RemovedDocumentReference,
  RemovedDraftDocumentIds,
} from '@/features/processo-arbitral/types/documentsTypes';
import type { RequerentePartyItem } from '@/features/processo-arbitral/types/requerenteStepTypes';
import type { RequeridoPartyItem } from '@/features/processo-arbitral/types/requeridoStepTypes';
import {
  defaultConflictDetailsFormData,
  getSuggestedRitoProcesso,
  RITO_PROCESSO_DEFAULT,
} from '@/features/processo-arbitral/utils/conflitoStepUtils';
import {
  defaultDocumentsFormData,
  filterRemovedDraftDocuments,
  syncDocumentsFormDataWithBackend,
} from '@/features/processo-arbitral/utils/documentosStepUtils';
import { normalizeCpfCnpj } from '@/shared/utils/documentValidation';

import { initializeProcessoArbitralDraft } from '../rascunhoStep/actions';

import {
  fetchCustasPorRito,
  fetchRequerenteRecentContacts,
  generateConflitoAiSuggestions,
  hydrateWizardDraft,
  removeConflictDocument,
  removeProofDocument,
  retryConflictDocument,
  retryProofDocument,
  saveRequerenteCatalogContact,
  saveRequeridoCatalogContact,
  saveWizardDraft,
  searchRequerenteContactByDocument,
  searchRequeridoContactByDocument,
  setConflictUploadProgress,
  setProofUploadProgress,
  syncWizardDraftDocuments,
  uploadConflictDocument,
  uploadProofDocument,
} from './actions';

interface SearchState {
  searchDocument: string;
  searchResult: ContactSearchResult | null;
  isSearching: boolean;
  searchError: string | null;
}

interface RequerenteWizardState extends SearchState {
  recentContacts: Contact[];
  isLoadingRecentContacts: boolean;
  /** Diferencia "nunca busquei" de "busquei e voltou vazio" — sem isto, o efeito de
   * auto-fetch em Step1ARequerente/Step1BRequerido (guard `!recentContacts.length`) repete
   * o dispatch a cada resolução de uma conta genuinamente sem contatos recentes, em loop
   * infinito (OOM do worker do vitest em `travaDuplicidadeSoRequerido.test.tsx`, e o mesmo
   * loop rodaria de verdade no browser de um usuário sem contatos). */
  hasFetchedRecentContacts: boolean;
  recentContactsError: string | null;
  parties: RequerentePartyItem[];
  isSavingCatalogContact: boolean;
  saveCatalogError: string | null;
}

interface RequeridoWizardState extends SearchState {
  parties: RequeridoPartyItem[];
  isSavingCatalogContact: boolean;
  saveCatalogError: string | null;
}

interface ConflitoWizardState {
  formData: typeof defaultConflictDetailsFormData;
  isGeneratingAi: boolean;
  aiSuggestions: PedidoSuggestion[];
  aiGenerationError: string | null;
}

interface DocumentosWizardState {
  formData: DocumentsFormData;
  removed: RemovedDraftDocumentIds;
}

interface CustasWizardState {
  /** Já com a condição comercial do contrato do escritório aplicada — ver `CustasPorRito`. */
  porRito: CustasPorRito;
  isLoading: boolean;
  error: string | null;
  /**
   * O fetch sai num efeito da revisão, depois do primeiro render: sem isto, esse quadro lia
   * `porRito` vazio como "rito sem preço" e mostrava "Definido na triagem" antes do valor.
   */
  hasFetched: boolean;
}

interface ProcessoArbitralWizardState {
  requerente: RequerenteWizardState;
  requerido: RequeridoWizardState;
  conflito: ConflitoWizardState;
  documentos: DocumentosWizardState;
  custas: CustasWizardState;
  draftId: string | null;
  isSavingDraft: boolean;
  draftSavedAt: string | null;
  isDraftHydrated: boolean;
}

const initialState: ProcessoArbitralWizardState = {
  requerente: {
    recentContacts: [],
    isLoadingRecentContacts: false,
    hasFetchedRecentContacts: false,
    recentContactsError: null,
    searchDocument: '',
    searchResult: null,
    isSearching: false,
    searchError: null,
    parties: [],
    isSavingCatalogContact: false,
    saveCatalogError: null,
  },
  requerido: {
    searchDocument: '',
    searchResult: null,
    isSearching: false,
    searchError: null,
    parties: [],
    isSavingCatalogContact: false,
    saveCatalogError: null,
  },
  conflito: {
    formData: defaultConflictDetailsFormData,
    isGeneratingAi: false,
    aiSuggestions: [],
    aiGenerationError: null,
  },
  documentos: {
    formData: defaultDocumentsFormData,
    removed: { requirementIds: [], fileIds: [], uploadIds: [], attemptIds: [] },
  },
  custas: {
    porRito: {},
    isLoading: false,
    error: null,
    hasFetched: false,
  },
  draftId: null,
  isSavingDraft: false,
  draftSavedAt: null,
  isDraftHydrated: false,
};

const resetProofRequirement = (document: ProofDocumentRequirement): ProofDocumentRequirement => ({
  ...document,
  uploadId: null,
  fileId: null,
  fileName: null,
  fileSize: undefined,
  mimeType: undefined,
  status: 'idle',
  uploadProgress: 0,
  error: undefined,
  uploadedAt: null,
  isRemoving: false,
});

const updateProofRequirement = (
  state: ProcessoArbitralWizardState,
  requirementId: string,
  updater: (document: ProofDocumentRequirement) => ProofDocumentRequirement,
) => {
  state.documentos.formData.comprovacao = state.documentos.formData.comprovacao.map((document) =>
    document.id === requirementId ? updater(document) : document,
  );
};

const updateConflictDocument = (
  state: ProcessoArbitralWizardState,
  documentId: string,
  updater: (document: ConflictDocument) => ConflictDocument,
) => {
  state.documentos.formData.conflito = state.documentos.formData.conflito.map((document) =>
    document.id === documentId ? updater(document) : document,
  );
};

const startProofUpload = (
  state: ProcessoArbitralWizardState,
  requirementId: string,
  attemptId: string,
  file?: File,
) => {
  updateProofRequirement(state, requirementId, (document) => ({
    ...document,
    attemptId,
    uploadId: null,
    fileId: null,
    fileName: file?.name ?? document.fileName,
    fileSize: file?.size ?? document.fileSize,
    mimeType: file?.type || document.mimeType,
    status: 'uploading',
    uploadProgress: 0,
    error: undefined,
    uploadedAt: null,
  }));
};

const finishProofUpload = (
  state: ProcessoArbitralWizardState,
  requirementId: string,
  payload: {
    uploadId: string;
    status: 'PENDENTE' | 'PROCESSANDO' | 'PROCESSADO' | 'ERRO';
    fileId: string | null;
    fileName: string;
    fileSize: number;
    mimeType?: string;
    error?: string | null;
  },
) => {
  updateProofRequirement(state, requirementId, (document) => ({
    ...document,
    uploadId: payload.uploadId,
    fileId: payload.fileId,
    fileName: payload.fileName,
    fileSize: payload.fileSize,
    mimeType: payload.mimeType,
    status:
      payload.status === 'PROCESSADO' ? 'done' : payload.status === 'ERRO' ? 'error' : 'processing',
    uploadProgress: 100,
    error: payload.error ?? undefined,
    uploadedAt: payload.status === 'PROCESSADO' ? new Date().toISOString() : null,
  }));
};

const failProofUpload = (
  state: ProcessoArbitralWizardState,
  requirementId: string,
  message: string,
) => {
  updateProofRequirement(state, requirementId, (document) => ({
    ...document,
    status: 'error',
    uploadProgress: 0,
    error: message,
  }));
};

const startConflictUpload = (
  state: ProcessoArbitralWizardState,
  documentId: string,
  attemptId: string,
  file?: File,
  subtipo?: ConflictDocumentSubtipo,
) => {
  const existingDocument = state.documentos.formData.conflito.find(
    (document) => document.id === documentId,
  );

  if (!existingDocument && file) {
    state.documentos.formData.conflito.push({
      id: documentId,
      subtipo,
      attemptId,
      uploadId: null,
      fileId: null,
      fileName: file.name,
      fileSize: file.size,
      mimeType: file.type || undefined,
      status: 'uploading',
      uploadProgress: 0,
      error: undefined,
      uploadedAt: null,
    });
    return;
  }

  updateConflictDocument(state, documentId, (document) => ({
    ...document,
    attemptId,
    uploadId: null,
    fileId: null,
    fileName: file?.name ?? document.fileName,
    fileSize: file?.size ?? document.fileSize,
    mimeType: file?.type || document.mimeType,
    status: 'uploading',
    uploadProgress: 0,
    error: undefined,
    uploadedAt: null,
  }));
};

const finishConflictUpload = (
  state: ProcessoArbitralWizardState,
  documentId: string,
  payload: {
    uploadId: string;
    status: 'PENDENTE' | 'PROCESSANDO' | 'PROCESSADO' | 'ERRO';
    fileId: string | null;
    fileName: string;
    fileSize: number;
    mimeType?: string;
    error?: string | null;
  },
) => {
  updateConflictDocument(state, documentId, (document) => ({
    ...document,
    uploadId: payload.uploadId,
    fileId: payload.fileId,
    fileName: payload.fileName,
    fileSize: payload.fileSize,
    mimeType: payload.mimeType,
    status:
      payload.status === 'PROCESSADO' ? 'done' : payload.status === 'ERRO' ? 'error' : 'processing',
    uploadProgress: 100,
    error: payload.error ?? undefined,
    uploadedAt: payload.status === 'PROCESSADO' ? new Date().toISOString() : null,
  }));
};

const failConflictUpload = (
  state: ProcessoArbitralWizardState,
  documentId: string,
  message: string,
) => {
  updateConflictDocument(state, documentId, (document) => ({
    ...document,
    status: 'error',
    uploadProgress: 0,
    error: message,
  }));
};

/**
 * Tira o requisito da lista de removidos quando um upload novo assume aquele
 * lugar.
 *
 * O `requirementId` de comprovação é determinístico
 * (`{polo}-{papel}-{cpfCnpj}-{tipo}`), então ele volta idêntico no reenvio. Sem
 * esquecê-lo aqui, o anexo que acabou de subir era tratado como "cancelado":
 * a compensação apagava o upload novo no backend e o card ficava em "Enviando…"
 * para sempre, travando a etapa — que exige comprovação para concluir.
 *
 * `fileIds`/`uploadIds` continuam: são ids já mortos no backend, e mantê-los é o
 * que impede o polling de ressuscitar o anexo antigo. O novo tem ids próprios.
 */
const forgetRemovedRequirement = (removed: RemovedDraftDocumentIds, requirementId: string) => {
  removed.requirementIds = removed.requirementIds.filter((id) => id !== requirementId);
};

const registerRemovedDocument = (
  removed: RemovedDraftDocumentIds,
  reference: RemovedDocumentReference,
) => {
  if (!removed.requirementIds.includes(reference.requirementId)) {
    removed.requirementIds.push(reference.requirementId);
  }

  if (reference.fileId && !removed.fileIds.includes(reference.fileId)) {
    removed.fileIds.push(reference.fileId);
  }

  if (reference.uploadId && !removed.uploadIds.includes(reference.uploadId)) {
    removed.uploadIds.push(reference.uploadId);
  }

  if (reference.attemptId && !removed.attemptIds.includes(reference.attemptId)) {
    removed.attemptIds.push(reference.attemptId);
  }
};

const ritoAcompanhavaSugestao = (formData: typeof defaultConflictDetailsFormData): boolean =>
  formData.ritoProcesso ===
  (getSuggestedRitoProcesso(formData.valorCausa) ?? RITO_PROCESSO_DEFAULT);

const aplicarRitoSugerido = (state: ProcessoArbitralWizardState) => {
  state.conflito.formData.ritoProcesso =
    getSuggestedRitoProcesso(state.conflito.formData.valorCausa) ?? RITO_PROCESSO_DEFAULT;
};

/**
 * Zera o que pertence a um rascunho específico quando o wizard passa a editar
 * outro.
 *
 * Sem isto, abrir um processo novo depois de mexer em outro rascunho mantinha os
 * documentos do anterior na tela — anexados na aparência, mas vinculados ao
 * rascunho errado no backend, então o processo novo nascia sem nenhum. Os
 * contatos recentes e as custas por rito sobrevivem porque são catálogo da
 * empresa, não do rascunho.
 */
const resetStateForDraft = (
  state: ProcessoArbitralWizardState,
  draftId: string,
): ProcessoArbitralWizardState => ({
  ...initialState,
  requerente: {
    ...initialState.requerente,
    recentContacts: state.requerente.recentContacts,
  },
  custas: state.custas,
  draftId,
});

const wizardStepSlice = createSlice({
  name: 'processoArbitralWizardStep',
  initialState,
  reducers: {
    setRequerenteSearchDocument: (state, action: PayloadAction<string>) => {
      state.requerente.searchDocument = action.payload;
      state.requerente.searchError = null;
      state.requerente.searchResult = null;
    },
    clearRequerenteSearchState: (state) => {
      state.requerente.searchDocument = '';
      state.requerente.searchResult = null;
      state.requerente.searchError = null;
      state.requerente.isSearching = false;
    },
    setRequerenteParties: (state, action: PayloadAction<RequerentePartyItem[]>) => {
      state.requerente.parties = action.payload;
    },
    addRequerenteParty: (state, action: PayloadAction<RequerentePartyItem>) => {
      state.requerente.parties.push(action.payload);
    },
    updateRequerenteParty: (
      state,
      action: PayloadAction<{ index: number; item: RequerentePartyItem }>,
    ) => {
      state.requerente.parties[action.payload.index] = action.payload.item;
    },
    removeRequerenteParty: (state, action: PayloadAction<number>) => {
      state.requerente.parties = state.requerente.parties.filter(
        (_, index) => index !== action.payload,
      );
    },
    confirmRequerentePartyRole: (state, action: PayloadAction<number>) => {
      const item = state.requerente.parties[action.payload];
      if (!item) {
        return;
      }
      item.pendingConfirmation = false;
      if (item.role === 'advogado' || item.role === 'representante') {
        item.party.representacaoDeclarada = true;
      }
    },
    setRequeridoSearchDocument: (state, action: PayloadAction<string>) => {
      state.requerido.searchDocument = action.payload;
      state.requerido.searchError = null;
      state.requerido.searchResult = null;
    },
    clearRequeridoSearchState: (state) => {
      state.requerido.searchDocument = '';
      state.requerido.searchResult = null;
      state.requerido.searchError = null;
      state.requerido.isSearching = false;
    },
    setRequeridoParties: (state, action: PayloadAction<RequeridoPartyItem[]>) => {
      state.requerido.parties = action.payload;
    },
    addRequeridoParty: (state, action: PayloadAction<RequeridoPartyItem>) => {
      state.requerido.parties.push(action.payload);
    },
    updateRequeridoParty: (
      state,
      action: PayloadAction<{ index: number; item: RequeridoPartyItem }>,
    ) => {
      state.requerido.parties[action.payload.index] = action.payload.item;
    },
    removeRequeridoParty: (state, action: PayloadAction<number>) => {
      state.requerido.parties = state.requerido.parties.filter(
        (_, index) => index !== action.payload,
      );
    },
    updateConflitoFormData: (
      state,
      action: PayloadAction<Partial<typeof defaultConflictDetailsFormData>>,
    ) => {
      const acompanhavaASugestao = ritoAcompanhavaSugestao(state.conflito.formData);

      state.conflito.formData = {
        ...state.conflito.formData,
        ...action.payload,
      };
      if (action.payload.pedidoLiminar === false) {
        state.conflito.formData.descricaoUrgencia = '';
      }

      const alterouValorSemTocarNoRito =
        'valorCausa' in action.payload && !('ritoProcesso' in action.payload);

      if (alterouValorSemTocarNoRito && acompanhavaASugestao) {
        aplicarRitoSugerido(state);
      }
    },
    clearConflitoField: (state, action: PayloadAction<'breveRelato' | 'descricaoPedido'>) => {
      state.conflito.formData[action.payload] = '';
    },
    clearConflitoAiState: (state) => {
      state.conflito.aiSuggestions = [];
      state.conflito.aiGenerationError = null;
      state.conflito.isGeneratingAi = false;
    },
    syncProofRequirements: (state, action: PayloadAction<ProofDocumentRequirement[]>) => {
      state.documentos.formData.comprovacao = action.payload;
    },
    resetWizardStepState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchRequerenteRecentContacts.pending, (state) => {
        state.requerente.isLoadingRecentContacts = true;
        state.requerente.recentContactsError = null;
      })
      .addCase(fetchRequerenteRecentContacts.fulfilled, (state, action) => {
        state.requerente.isLoadingRecentContacts = false;
        state.requerente.hasFetchedRecentContacts = true;
        state.requerente.recentContacts = action.payload;
      })
      .addCase(fetchRequerenteRecentContacts.rejected, (state, action) => {
        state.requerente.isLoadingRecentContacts = false;
        state.requerente.hasFetchedRecentContacts = true;
        state.requerente.recentContactsError =
          action.payload || 'Erro ao carregar contatos recentes';
      })
      .addCase(searchRequerenteContactByDocument.pending, (state, action) => {
        state.requerente.isSearching = true;
        state.requerente.searchError = null;
        state.requerente.searchDocument = action.meta.arg;
      })
      .addCase(searchRequerenteContactByDocument.fulfilled, (state, action) => {
        state.requerente.isSearching = false;
        state.requerente.searchResult = action.payload;
      })
      .addCase(searchRequerenteContactByDocument.rejected, (state, action) => {
        state.requerente.isSearching = false;
        state.requerente.searchError = action.payload || 'Erro ao buscar contato';
      })
      .addCase(saveRequerenteCatalogContact.pending, (state) => {
        state.requerente.isSavingCatalogContact = true;
        state.requerente.saveCatalogError = null;
      })
      .addCase(saveRequerenteCatalogContact.fulfilled, (state, action) => {
        state.requerente.isSavingCatalogContact = false;
        state.requerente.saveCatalogError = null;
        const existingIndex = state.requerente.recentContacts.findIndex(
          (contact) =>
            normalizeCpfCnpj(contact.cpfCnpj) === normalizeCpfCnpj(action.payload.cpfCnpj),
        );
        if (existingIndex >= 0) {
          state.requerente.recentContacts[existingIndex] = action.payload;
          return;
        }
        state.requerente.recentContacts.unshift(action.payload);
      })
      .addCase(saveRequerenteCatalogContact.rejected, (state, action) => {
        state.requerente.isSavingCatalogContact = false;
        state.requerente.saveCatalogError = action.payload || 'Erro ao salvar contato no catálogo';
      })
      .addCase(searchRequeridoContactByDocument.pending, (state, action) => {
        state.requerido.isSearching = true;
        state.requerido.searchError = null;
        state.requerido.searchDocument = action.meta.arg;
      })
      .addCase(searchRequeridoContactByDocument.fulfilled, (state, action) => {
        state.requerido.isSearching = false;
        state.requerido.searchResult = action.payload;
      })
      .addCase(searchRequeridoContactByDocument.rejected, (state, action) => {
        state.requerido.isSearching = false;
        state.requerido.searchError = action.payload || 'Erro ao buscar contato';
      })
      .addCase(saveRequeridoCatalogContact.pending, (state) => {
        state.requerido.isSavingCatalogContact = true;
        state.requerido.saveCatalogError = null;
      })
      .addCase(saveRequeridoCatalogContact.fulfilled, (state) => {
        state.requerido.isSavingCatalogContact = false;
        state.requerido.saveCatalogError = null;
      })
      .addCase(saveRequeridoCatalogContact.rejected, (state, action) => {
        state.requerido.isSavingCatalogContact = false;
        state.requerido.saveCatalogError = action.payload || 'Erro ao salvar contato no catálogo';
      })
      .addCase(fetchCustasPorRito.pending, (state) => {
        state.custas.isLoading = true;
        state.custas.error = null;
      })
      .addCase(fetchCustasPorRito.fulfilled, (state, action) => {
        state.custas.isLoading = false;
        state.custas.porRito = action.payload;
        state.custas.hasFetched = true;
      })
      .addCase(fetchCustasPorRito.rejected, (state, action) => {
        state.custas.isLoading = false;
        state.custas.error = action.payload || 'Erro ao carregar as custas da câmara';
      })
      .addCase(initializeProcessoArbitralDraft.fulfilled, (state, action) => {
        if (state.draftId === action.payload.id) {
          return state;
        }

        if (!state.draftId) {
          state.draftId = action.payload.id;
          return state;
        }

        return resetStateForDraft(state, action.payload.id);
      })
      .addCase(hydrateWizardDraft.fulfilled, (state, action) => {
        if (!action.payload) {
          state.isDraftHydrated = false;
          return;
        }
        state.isDraftHydrated = true;
        state.requerente.parties = action.payload.snapshot.requerentes;
        state.requerido.parties = action.payload.snapshot.requeridos;
        state.conflito.formData = action.payload.snapshot.conflito;
        if (!action.payload.snapshot.conflito.ritoProcesso) {
          aplicarRitoSugerido(state);
        }
        state.draftSavedAt = action.payload.updatedAt;
      })
      .addCase(syncWizardDraftDocuments.fulfilled, (state, action) => {
        if (!action.payload || action.payload.draftId !== state.draftId) {
          return;
        }

        const visiveis = filterRemovedDraftDocuments(
          action.payload.documents,
          action.payload.uploads,
          state.documentos.removed,
        );

        state.documentos.formData = syncDocumentsFormDataWithBackend(
          state.documentos.formData,
          visiveis.documents,
          visiveis.uploads,
        );
      })
      .addCase(saveWizardDraft.pending, (state) => {
        state.isSavingDraft = true;
      })
      .addCase(saveWizardDraft.fulfilled, (state, action) => {
        state.isSavingDraft = false;
        state.draftSavedAt = action.payload.updatedAt;
      })
      .addCase(saveWizardDraft.rejected, (state) => {
        state.isSavingDraft = false;
      })
      .addCase(generateConflitoAiSuggestions.pending, (state) => {
        state.conflito.isGeneratingAi = true;
        state.conflito.aiGenerationError = null;
      })
      .addCase(generateConflitoAiSuggestions.fulfilled, (state, action) => {
        state.conflito.isGeneratingAi = false;
        state.conflito.aiSuggestions = action.payload;
      })
      .addCase(generateConflitoAiSuggestions.rejected, (state, action) => {
        state.conflito.isGeneratingAi = false;
        state.conflito.aiSuggestions = [];
        state.conflito.aiGenerationError = action.payload || 'Erro ao gerar sugestões de pedido';
      })
      .addCase(setProofUploadProgress, (state, action) => {
        updateProofRequirement(state, action.payload.requirementId, (document) => ({
          ...document,
          uploadProgress: action.payload.uploadProgress,
        }));
      })
      .addCase(setConflictUploadProgress, (state, action) => {
        updateConflictDocument(state, action.payload.documentId, (document) => ({
          ...document,
          uploadProgress: action.payload.uploadProgress,
        }));
      })
      .addCase(uploadProofDocument.pending, (state, action) => {
        forgetRemovedRequirement(state.documentos.removed, action.meta.arg.requirementId);
        startProofUpload(
          state,
          action.meta.arg.requirementId,
          action.meta.requestId,
          action.meta.arg.file,
        );
      })
      .addCase(uploadProofDocument.fulfilled, (state, action) => {
        finishProofUpload(state, action.payload.requirementId, action.payload.result);
      })
      .addCase(uploadProofDocument.rejected, (state, action) => {
        if (!action.payload || action.payload.failure.code === 'aborted') {
          return;
        }
        failProofUpload(state, action.payload.requirementId, action.payload.failure.message);
      })
      .addCase(retryProofDocument.pending, (state, action) => {
        forgetRemovedRequirement(state.documentos.removed, action.meta.arg.requirementId);
        startProofUpload(state, action.meta.arg.requirementId, action.meta.requestId);
      })
      .addCase(retryProofDocument.fulfilled, (state, action) => {
        finishProofUpload(state, action.payload.requirementId, action.payload.result);
      })
      .addCase(retryProofDocument.rejected, (state, action) => {
        if (!action.payload || action.payload.failure.code === 'aborted') {
          return;
        }
        failProofUpload(state, action.payload.requirementId, action.payload.failure.message);
      })
      .addCase(uploadConflictDocument.pending, (state, action) => {
        forgetRemovedRequirement(state.documentos.removed, action.meta.arg.documentId);
        startConflictUpload(
          state,
          action.meta.arg.documentId,
          action.meta.requestId,
          action.meta.arg.file,
          action.meta.arg.subtipo,
        );
      })
      .addCase(uploadConflictDocument.fulfilled, (state, action) => {
        finishConflictUpload(state, action.payload.documentId, action.payload.result);
      })
      .addCase(uploadConflictDocument.rejected, (state, action) => {
        if (!action.payload || action.payload.failure.code === 'aborted') {
          return;
        }
        failConflictUpload(state, action.payload.documentId, action.payload.failure.message);
      })
      .addCase(retryConflictDocument.pending, (state, action) => {
        forgetRemovedRequirement(state.documentos.removed, action.meta.arg.documentId);
        startConflictUpload(state, action.meta.arg.documentId, action.meta.requestId);
      })
      .addCase(retryConflictDocument.fulfilled, (state, action) => {
        finishConflictUpload(state, action.payload.documentId, action.payload.result);
      })
      .addCase(retryConflictDocument.rejected, (state, action) => {
        if (!action.payload || action.payload.failure.code === 'aborted') {
          return;
        }
        failConflictUpload(state, action.payload.documentId, action.payload.failure.message);
      })
      .addCase(removeProofDocument.pending, (state, action) => {
        updateProofRequirement(state, action.meta.arg.requirementId, (document) => ({
          ...document,
          isRemoving: true,
          error: undefined,
        }));
      })
      .addCase(removeProofDocument.fulfilled, (state, action) => {
        registerRemovedDocument(state.documentos.removed, action.payload.removed);
        updateProofRequirement(state, action.payload.requirementId, (document) =>
          resetProofRequirement(document),
        );
      })
      .addCase(removeProofDocument.rejected, (state, action) => {
        if (!action.payload) {
          return;
        }

        updateProofRequirement(state, action.payload.requirementId, (document) => ({
          ...document,
          isRemoving: false,
          error: action.payload?.failure.message,
        }));
      })
      .addCase(removeConflictDocument.pending, (state, action) => {
        updateConflictDocument(state, action.meta.arg.documentId, (document) => ({
          ...document,
          isRemoving: true,
          error: undefined,
        }));
      })
      .addCase(removeConflictDocument.fulfilled, (state, action) => {
        registerRemovedDocument(state.documentos.removed, action.payload.removed);
        state.documentos.formData.conflito = state.documentos.formData.conflito.filter(
          (document) => document.id !== action.payload.documentId,
        );
      })
      .addCase(removeConflictDocument.rejected, (state, action) => {
        if (!action.payload) {
          return;
        }

        updateConflictDocument(state, action.payload.documentId, (document) => ({
          ...document,
          isRemoving: false,
          error: action.payload?.failure.message,
        }));
      });
  },
});

export const {
  addRequerenteParty,
  addRequeridoParty,
  clearConflitoAiState,
  clearConflitoField,
  clearRequerenteSearchState,
  clearRequeridoSearchState,
  confirmRequerentePartyRole,
  removeRequerenteParty,
  removeRequeridoParty,
  resetWizardStepState,
  setRequerenteParties,
  setRequerenteSearchDocument,
  setRequeridoParties,
  setRequeridoSearchDocument,
  syncProofRequirements,
  updateConflitoFormData,
  updateRequerenteParty,
  updateRequeridoParty,
} = wizardStepSlice.actions;

export const wizardStepReducer = wizardStepSlice.reducer;
